import { z } from "zod";
import { UNIT_CODES } from "../units";
import { pageQuery } from "./common";

/**
 * Заявки на закупку: сотрудник (повар, бармен, кассир) пишет, что нужно купить, с заметкой; управляющий одобряет или отказывает,
 * после закупки заявка закрывается — со ссылкой на закупку или просто отметкой «Куплено».
 */
export const PURCHASE_REQUEST_STATUSES = ["submitted", "approved", "purchased", "rejected", "cancelled"] as const;
export type PurchaseRequestStatus = (typeof PURCHASE_REQUEST_STATUSES)[number];
export const PURCHASE_REQUEST_PRIORITIES = ["normal", "urgent"] as const;
export type PurchaseRequestPriority = (typeof PURCHASE_REQUEST_PRIORITIES)[number];
/** Заявку ещё можно изменить или отозвать */
export const PURCHASE_REQUEST_OPEN: readonly PurchaseRequestStatus[] = ["submitted", "approved"];

const isoDate = z.iso.date({ error: "Дата в формате ГГГГ-ММ-ДД" });
const note = (max: number) => z.string().trim().max(max, { error: `Максимум ${max} символов` }).nullable().optional();

const line = z
  .strictObject({
    /** Продукт из справочника; нет в справочнике — только название */
    productId: z.uuid().nullable().optional(),
    name: z.string().trim().max(200, { error: "Максимум 200 символов" }).nullable().optional(),
    qty: z.number({ error: "Укажите количество" }).positive({ error: "Количество должно быть больше нуля" }).max(1e9),
    unit: z.enum(UNIT_CODES as [string, ...string[]], { error: "Неизвестная единица" }),
    /** Заметка к строке: марка, жирность, «как в прошлый раз» */
    note: note(300),
  })
  .refine((l) => !!l.productId || !!l.name, { error: "Выберите продукт или напишите название", path: ["name"] });

export const purchaseRequestSchema = z.strictObject({
  priority: z.enum(PURCHASE_REQUEST_PRIORITIES).default("normal"),
  neededBy: isoDate.nullable().optional(),
  /** Заметка автора: зачем, к какому событию, где купить */
  comment: note(1000),
  lines: z.array(line).min(1, { error: "Добавьте хотя бы одну строку" }).max(100),
});
export type PurchaseRequestInput = z.input<typeof purchaseRequestSchema>;
export const purchaseRequestUpdateSchema = purchaseRequestSchema.extend({ version: z.number().int().min(1) });

/** Одобрить: заметка для автора и закупщика */
export const purchaseRequestApproveSchema = z.strictObject({ note: note(1000) });
export const purchaseRequestRejectSchema = z.strictObject({ reason: z.string().trim().min(1, { error: "Укажите причину" }).max(1000, { error: "Максимум 1000 символов" }) });
/** Куплено: закупка (документ прихода), по которой закрыли заявку, — необязательно */
export const purchaseRequestPurchasedSchema = z.strictObject({ stockDocumentId: z.uuid().nullable().optional(), note: note(1000) });

const flag = z.enum(["true", "false"]).transform((v) => v === "true");
export const purchaseRequestsQuery = pageQuery.extend({
  status: z.enum(PURCHASE_REQUEST_STATUSES).optional(),
  /** Только мои заявки */
  mine: flag.optional(),
});

/**
 * Рекомендации к закупке: фактический расход по складу филиала (продажи по техкартам, списания, сырьё в производство)
 * за `historyDays` дней → прогноз в день (с учётом последней недели) → сколько заказать на `coverDays` дней с запасом `safetyPct`,
 * за вычетом остатка и уже заказанного в открытых заявках; минимальный остаток продукта держится сверху.
 */
export const PURCHASE_SUGGESTION_REASONS = ["negative", "below_min", "runs_out", "low_data"] as const;
export type PurchaseSuggestionReason = (typeof PURCHASE_SUGGESTION_REASONS)[number];
export const purchaseSuggestionsQuery = z.object({
  /** На сколько дней закупить */
  coverDays: z.coerce.number().int().min(1, { error: "От 1 до 60 дней" }).max(60, { error: "От 1 до 60 дней" }).default(7),
  /** За сколько дней смотреть расход */
  historyDays: z.coerce.number().int().min(7, { error: "От 7 до 90 дней" }).max(90, { error: "От 7 до 90 дней" }).default(28),
  /** Запас сверх прогноза, % */
  safetyPct: z.coerce.number().int().min(0).max(200).default(15),
  /** Все продукты с расходом, а не только те, что пора заказать */
  all: flag.optional(),
});
